import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Hook consolidado para verificar y gestionar permisos de Super Admin
 * 
 * ARQUITECTURA SAAS:
 * - Super Admin: Dueño del sistema (control total cross-company)
 * - Admin: Administrador de empresa (control total de su empresa)
 */
export function useSuperAdmin() {
  const { user } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isChecking, setIsChecking] = useState(false);

  const checkSuperAdminStatus = useCallback(async (): Promise<boolean> => {
    if (!user?.id) {
      setIsSuperAdmin(false);
      return false;
    }

    setIsChecking(true);
    try {
      console.log('🔐 [SYSTEM OWNER] Verificando Super Admin para:', user.id);
      
      // Método 1: Función RPC optimizada
      const { data: superAdminCheck, error: rpcError } = await supabase
        .rpc('is_user_super_admin', { _user_id: user.id });

      if (rpcError) {
        console.error('❌ Error en RPC super admin:', rpcError);
        
        // Método 2: Fallback directo a user_roles
        const { data: userRoles, error: rolesError } = await supabase
          .from('user_roles')
          .select(`
            id, 
            is_active,
            company_id,
            roles!inner(code, name)
          `)
          .eq('user_id', user.id)
          .eq('is_active', true);

        if (rolesError) {
          console.error('❌ Error verificando roles:', rolesError);
          return false;
        }

        const isSuperAdminRole = userRoles?.some(ur => ur.roles.code === 'super_admin');
        setIsSuperAdmin(isSuperAdminRole || false);
        
        if (isSuperAdminRole) {
          console.log('✅ [SYSTEM OWNER] Super Admin confirmado via fallback');
        }
        
        return isSuperAdminRole || false;
      }

      const isSuper = superAdminCheck || false;
      setIsSuperAdmin(isSuper);
      
      if (isSuper) {
        console.log('✅ [SYSTEM OWNER] Super Admin confirmado - Control total del sistema');
      } else {
        console.log('👤 Usuario regular - Permisos limitados a su empresa');
      }

      return isSuper;
      
    } catch (error) {
      console.error('❌ Error crítico en verificación Super Admin:', error);
      setIsSuperAdmin(false);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, [user?.id]);

  useEffect(() => {
    const performCheck = async () => {
      setLoading(true);
      await checkSuperAdminStatus();
      setLoading(false);
    };

    performCheck();
  }, [checkSuperAdminStatus]);

  return { 
    isSuperAdmin, 
    loading, 
    isChecking,
    checkSuperAdminStatus 
  };
}